import {
    Checkbox,
    Col,
    Collapse,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
    Switch,
    Tag,
} from "antd";
import { observer } from "mobx-react-lite";
import React from "react";
import MetadataDefinitionStore from "../stores/MetadataDefinitionStore";
import { MetadataFieldType } from "../types/Metadata";
import { FormInstance } from "antd/es/form";
import {
    ONLY_DATE,
    FULL_DATE,
    ONLY_TIME,
    Utils,
} from "../../common/misc/Utils";
import MetadataListItemsEditor from "./MetadataListItemsEditor";
const FormItem = Form.Item;

type Props = {
    store: MetadataDefinitionStore;
    form: FormInstance;
};

const MetadataDefinitionEditForm: React.FC<Props> = ({ store, form }) => {
    const mode = store.editDialogMode;
    const [collapseActiveKey, setCollapseActiveKey] = React.useState<string[]>(
        [],
    );
    const [isCalculationEnabled, setIsCalculationEnabled] = React.useState(
        store.selectedDefinition?.calculation?.enabled || false,
    );
    const isMultiValue = Form.useWatch("isMultiValue", form) ?? false;

    React.useEffect(() => {
        setIsCalculationEnabled(
            store.selectedDefinition?.calculation?.enabled || false,
        );
    }, [store.selectedDefinition]);

    React.useEffect(() => {
        if (store.selectedDefinition) {
            const metadata = store.selectedDefinition;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const formValues: any = {
                ...metadata,
            };

            // Parse format for non-DateTime types
            if (metadata.format && metadata.fieldType !== "DateTime") {
                const parsedFormat = JSON.parse(metadata.format);
                Object.assign(formValues, parsedFormat);
            }

            // Handle calculation data separately (available for all types)
            if (metadata.calculation) {
                formValues.calculation = metadata.calculation;
                setCollapseActiveKey(["1"]);
            } else {
                setCollapseActiveKey([]);
            }

            form.setFieldsValue(formValues);
        }
    }, [store.editDefinitionDialogVisible, store.selectedDefinition, form]);

    const handleSubmit = () => {
        form.validateFields()
            .then((formData) => {
                switch (store.selectedDefinition?.fieldType) {
                    case "Text":
                        formData.format = JSON.stringify({
                            prefix: formData.prefix ?? null,
                            suffix: formData.suffix ?? null,
                        });
                        break;
                    case "Boolean":
                        formData.format = JSON.stringify({
                            trueVal: formData.trueVal ?? null,
                            falseVal: formData.falseVal ?? null,
                        });
                        if (!!formData.trueVal !== !!formData.falseVal) {
                            // reset previous errors
                            form.setFields([
                                {
                                    name: "trueVal",
                                    errors: [],
                                },
                                {
                                    name: "falseVal",
                                    errors: [],
                                },
                            ]);
                            form.setFields([
                                {
                                    name: !formData.trueVal
                                        ? "trueVal"
                                        : "falseVal",
                                    errors: [
                                        "Either both values are required or none",
                                    ],
                                },
                            ]);
                            return;
                        }
                        break;
                    case "Number":
                        formData.format = JSON.stringify({
                            precision: formData.precision,
                            separator: formData.separator,
                        });
                        if (formData.calculation) {
                            formData.calculation.enabled = isCalculationEnabled;
                        }
                        break;
                    default:
                        break;
                }

                if (mode === "create") {
                    store.createMetadataDefinition(formData);
                } else {
                    store.updateMetadataDefinition(
                        formData.title,
                        formData.description,
                        formData.format,
                        formData.isVisible,
                        formData.isEditable,
                        formData.isMultiValue,
                        formData.taskTypes,
                        formData.listValues,
                        formData.calculation,
                    );
                }
                form.resetFields();
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleTypeChange = (val: MetadataFieldType) => {
        if (val !== "Text") {
            form.setFieldsValue({ listItems: [] });
        }

        if (val === "DateTime") {
            form.setFieldsValue({ format: ONLY_DATE });
        }

        store.handleTypeChange(val);
    };

    // Get only Number type metadata fields for calculations
    const numberMetadataFields = store.metadataDefinitions
        .filter(
            (m) =>
                m.fieldType === "Number" &&
                !m.isMultiValue &&
                m.calculation?.enabled !== true,
        )
        .map((m) => ({
            label: m.title,
            value: m.id,
        }));

    const operatorOptions = [
        { label: "+", value: "+" },
        { label: "-", value: "-" },
        { label: "*", value: "*" },
        { label: "/", value: "/" },
    ];

    const renderTaskTypeLabel = (name: string, isSystemDefault?: boolean) => (
        <>
            {name}
            {isSystemDefault ? (
                <Tag className="alpha-info-tag" style={{ marginLeft: 8 }}>
                    System managed
                </Tag>
            ) : null}
        </>
    );

    const sections = [
        {
            key: "1",
            label: "Calculation settings",
            children: (
                <Space direction="vertical" style={{ width: "100%" }}>
                    <FormItem
                        className="dialog-field"
                        name={["calculation", "fieldA"]}
                        label={
                            <span className="dialog-field-label">Field A</span>
                        }
                        style={{ marginBottom: 8 }}
                        rules={[
                            {
                                required: isCalculationEnabled,
                                message: "Field A is required",
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select a number field"
                            options={numberMetadataFields}
                            style={{ width: "100%" }}
                            allowClear
                        />
                    </FormItem>

                    <FormItem
                        name={["calculation", "operator"]}
                        label={
                            <span className="dialog-field-label">Operator</span>
                        }
                        rules={[
                            {
                                required: isCalculationEnabled,
                                message: "Operator is required",
                            },
                        ]}
                        style={{ marginBottom: 8 }}
                    >
                        <Select
                            placeholder="Select operator"
                            options={operatorOptions}
                            style={{ width: "100%" }}
                            allowClear
                        />
                    </FormItem>

                    <FormItem
                        name={["calculation", "fieldB"]}
                        label={
                            <span className="dialog-field-label">Field B</span>
                        }
                        style={{ marginBottom: 8 }}
                        rules={[
                            {
                                required: isCalculationEnabled,
                                message: "Field B is required",
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select a number field"
                            options={numberMetadataFields}
                            style={{ width: "100%" }}
                            allowClear
                        />
                    </FormItem>

                    <FormItem
                        name={["calculation", "operator2"]}
                        label="Operator 2"
                        style={{ marginBottom: 8 }}
                        tooltip="Used only if Constant is filled"
                    >
                        <Select
                            placeholder="Select operator"
                            options={operatorOptions}
                            style={{ width: "100%" }}
                            allowClear
                        />
                    </FormItem>

                    <FormItem
                        name={["calculation", "constant"]}
                        label="Constant"
                        style={{ marginBottom: 8 }}
                        tooltip="Used only with Operator 2"
                    >
                        <InputNumber
                            placeholder="Enter a number"
                            style={{ width: "100%" }}
                            step={0.01}
                        />
                    </FormItem>

                    <div style={{ fontSize: "12px", color: "#888" }}>
                        Calculated value = Field A [operator] Field B
                        [operator2] Constant
                    </div>
                </Space>
            ),
        },
    ];

    const getFormatTypeRenderer = () => {
        switch (store.selectedDefinition?.fieldType) {
            case "Text":
                return (
                    <Space>
                        <FormItem name="prefix" style={{ marginBottom: 0 }}>
                            <Input
                                placeholder="prefix"
                                style={{ width: 100 }}
                            />
                        </FormItem>
                        <div>{"{value}"}</div>
                        <FormItem name="suffix" style={{ marginBottom: 0 }}>
                            <Input
                                placeholder="suffix"
                                style={{ width: 100 }}
                            />
                        </FormItem>
                    </Space>
                );
            case "DateTime":
                return (
                    <FormItem name="format" style={{ marginBottom: 0 }}>
                        <Select
                            options={[
                                { label: "Full date", value: FULL_DATE },
                                { label: "Short date", value: ONLY_DATE },
                                { label: "Only time", value: ONLY_TIME },
                            ]}
                        />
                    </FormItem>
                );
            case "Boolean":
                return (
                    <Space>
                        <FormItem name="trueVal" style={{ marginBottom: 0 }}>
                            <Input style={{ width: 100 }} placeholder="true" />
                        </FormItem>
                        <div>{"/"}</div>
                        <FormItem name="falseVal" style={{ marginBottom: 0 }}>
                            <Input style={{ width: 100 }} placeholder="false" />
                        </FormItem>
                    </Space>
                );
            case "Number":
                return (
                    <Space>
                        <FormItem name="precision" style={{ marginBottom: 0 }}>
                            <InputNumber
                                max={10}
                                min={0}
                                style={{ width: 100 }}
                                placeholder="precision"
                            />
                        </FormItem>
                        <FormItem
                            name="separator"
                            style={{ marginBottom: 0 }}
                            valuePropName="checked"
                        >
                            <Checkbox>Thousands separator</Checkbox>
                        </FormItem>
                    </Space>
                );
            default:
                return <></>;
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadataKeyValidator = (rule: any, value: any, callback: any) => {
        if (value && /\s/.test(value)) {
            callback("Key must not contain spaces");
            return;
        }
        if (value && /^_/.test(value)) {
            callback("Key must not start with an underscore");
            return;
        }
        if (value && !/^[a-zA-Z0-9_]+$/.test(value)) {
            callback(
                "Key may only contain alphanumeric characters and underscores",
            );
            return;
        }
        const metadataToValidate = store!.metadataDefinitions.map((m) =>
            m.name.toLowerCase(),
        );
        if (
            value &&
            metadataToValidate.includes(value.toLowerCase()) &&
            store.selectedDefinition?.name.toLowerCase() !== value.toLowerCase()
        ) {
            callback("Metadata with the same key already exists");
        } else {
            callback();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const metadataTitleValidator = (rule: any, value: any, callback: any) => {
        const metadataToValidate = store!.metadataDefinitions.map((m) =>
            m.title.toLowerCase(),
        );
        if (
            value &&
            metadataToValidate.includes(value.toLowerCase()) &&
            store.selectedDefinition?.title.toLowerCase() !==
                value.toLowerCase()
        ) {
            callback("Metadata with the same title already exists");
        } else {
            callback();
        }
    };

    return (
        <div className="dialog-section">
            <Form
                form={form}
                onFinish={handleSubmit}
                className="inputs-container metadata-definition-form"
                id="edit-metadata-definition-form"
                layout="vertical"
            >
                <FormItem name="projectId" hidden />
                <FormItem
                    name="name"
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Key</span>}
                    rules={[
                        { required: true, message: "Key is required" },
                        { validator: metadataKeyValidator },
                    ]}
                >
                    <Input disabled={mode === "edit"} />
                </FormItem>
                <FormItem
                    name="title"
                    colon={false}
                    className="dialog-field"
                    label={<span className="dialog-field-label">Title</span>}
                    rules={[
                        { required: true, message: "Title is required" },
                        { validator: metadataTitleValidator },
                    ]}
                >
                    <Input />
                </FormItem>
                <Row gutter={24}>
                    <Col span={8}>
                        <FormItem
                            name="fieldType"
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Field type
                                </span>
                            }
                        >
                            <Select
                                suffixIcon={
                                    <i
                                        className="alpha-icon xxs arrow-down-icon"
                                        style={{ margin: 0, width: 8 }}
                                    />
                                }
                                disabled={mode === "edit"}
                                onChange={handleTypeChange}
                            >
                                <Select.Option value="Text">Text</Select.Option>
                                <Select.Option value="Number">
                                    Number
                                </Select.Option>
                                <Select.Option value="Boolean">
                                    Boolean
                                </Select.Option>
                                <Select.Option value="DateTime">
                                    Date
                                </Select.Option>
                            </Select>
                        </FormItem>
                    </Col>
                    <Col span={16}>
                        <FormItem
                            colon={false}
                            className="dialog-field"
                            label={
                                <span className="dialog-field-label">
                                    Format
                                </span>
                            }
                        >
                            {getFormatTypeRenderer()}
                        </FormItem>
                    </Col>
                </Row>
                {store.selectedDefinition?.fieldType === "Text" && (
                    <Form.List name="listValues">
                        {(items, { add, remove }) => (
                            <MetadataListItemsEditor
                                form={form}
                                items={items}
                                add={add}
                                remove={remove}
                            />
                        )}
                    </Form.List>
                )}

                {store.selectedDefinition?.fieldType === "Number" && (
                    <div style={{ marginBottom: 16 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: 16,
                            }}
                        >
                            <span style={{ marginRight: 8 }}>
                                Enable calculation
                            </span>
                            <Switch
                                checked={isCalculationEnabled}
                                disabled={isMultiValue}
                                onChange={(checked) => {
                                    setIsCalculationEnabled(checked);
                                    if (!checked) {
                                        setCollapseActiveKey([]);
                                    } else {
                                        // Expand when enabled
                                        setCollapseActiveKey(["1"]);
                                    }
                                }}
                            />
                        </div>
                        <Collapse
                            items={sections}
                            bordered={false}
                            activeKey={
                                isCalculationEnabled ? collapseActiveKey : []
                            }
                            onChange={(keys) => {
                                if (isCalculationEnabled) {
                                    setCollapseActiveKey(keys as string[]);
                                }
                            }}
                            collapsible={
                                isCalculationEnabled ? "header" : "disabled"
                            }
                        />
                    </div>
                )}
                <FormItem
                    name="taskTypes"
                    colon={false}
                    className="dialog-field"
                    tooltip="If no task type is selected, the metadata will be displayed for all types of tasks"
                    label="Task types"
                >
                    <Select
                        mode="multiple"
                        options={store.taskTypes.map((t) => ({
                            value: t.id,
                            label: renderTaskTypeLabel(
                                t.name,
                                t.isSystemDefault,
                            ),
                            customFilterValue: t.name,
                        }))}
                        filterOption={Utils.filterOption}
                        showSearch
                    />
                </FormItem>
                <FormItem
                    name="description"
                    colon={false}
                    className="dialog-field"
                    label={
                        <span className="dialog-field-label">Description</span>
                    }
                >
                    <Input />
                </FormItem>
                <Space size="large">
                    <FormItem
                        name="isVisible"
                        valuePropName="checked"
                        tooltip="If checked the regular user will see metadata, otherwise only admin will see it"
                        label="Is visible"
                    >
                        <Checkbox />
                    </FormItem>
                    <FormItem
                        name="isEditable"
                        valuePropName="checked"
                        tooltip="If checked the regular user will be able to edit the value of this metadata field, otherwise only admin will be able to do that."
                        label="Is editable"
                    >
                        <Checkbox />
                    </FormItem>
                    <FormItem
                        name="isMultiValue"
                        valuePropName="checked"
                        tooltip="Stores typed values as an array. Actions that use this field require the user to select one value."
                        label="Allow multiple values"
                    >
                        <Checkbox
                            disabled={
                                mode === "edit" &&
                                store.selectedDefinition?.isMultiValue
                            }
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setIsCalculationEnabled(false);
                                    setCollapseActiveKey([]);
                                    form.setFieldValue(
                                        ["calculation", "enabled"],
                                        false,
                                    );
                                }
                            }}
                        />
                    </FormItem>
                </Space>
            </Form>
        </div>
    );
};

export default observer(MetadataDefinitionEditForm);
